import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, UserX, Timer, AlertCircle, CheckCircle2, Calendar, Receipt, Zap, Plus, ArrowRight } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { StatCard } from '@/components/shared/StatCard';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { selectCompanyName, selectCompanyPlan, selectCompany } from '@/store/slices/companySlice';
import { selectUser } from '@/store/slices/authSlice';
import { format } from 'date-fns';
import { useDashboardStats } from '@/api/queries/dashboard.queries';

const PLAN_META = {
  BASIC:  { label: 'Basic Plan',  color: '#6C6C70', bg: 'rgba(108,108,112,0.1)' },
  PRO:    { label: 'Pro Plan',    color: '#007AFF', bg: 'rgba(0,122,255,0.1)'   },
  CUSTOM: { label: 'Custom Plan', color: '#AF52DE', bg: 'rgba(175,82,222,0.1)'  },
};

const DONUT_COLORS = ['#007AFF','#FF3B30','#34C759','#FF9500'];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--bg-elevated)] border border-[var(--border)] rounded-[10px] px-3 py-2 shadow-[var(--shadow-md)]">
      <p className="text-[12px] font-semibold text-[var(--text-primary)] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-[12px] text-[var(--text-secondary)]">
          <span style={{color:p.color}}>●</span> {p.name}: <strong>{typeof p.value === 'number' && p.value > 10000 ? `₹${(p.value/100000).toFixed(1)}L` : p.value}{p.dataKey==='percentage'?'%':''}</strong>
        </p>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  const companyName = useSelector(selectCompanyName);
  const plan = useSelector(selectCompanyPlan);
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const today = format(new Date(), 'EEEE, d MMMM yyyy');
  const planMeta = PLAN_META[plan] || PLAN_META.BASIC;

  const { data: statsResp } = useDashboardStats();
  const stats = statsResp?.data || {};
  const s = stats.summary || {};
  const attendanceTrend = stats.attendance_trend || [];
  const payrollTrend = stats.payroll_trend || [];
  const leaveDistribution = stats.leave_distribution || [];
  const pendingActions = stats.pending_actions || [];
  const newJoiners = stats.new_joiners || [];

  const isEmpty = !s.total_employees;
  const presentPct = s.total_employees ? Math.round((s.present / s.total_employees) * 100) : 0;
  const donutData = [
    { name: 'Present', value: s.present || 0 },
    { name: 'Absent',  value: s.absent || 0 },
    { name: 'Leave',   value: s.on_leave || 0 },
    { name: 'Late',    value: s.late_arrivals || 0 },
  ];
  const actionIcons = { leave: Calendar, expense: Receipt, regularize: Clock };

  return (
    <div className="space-y-6 fade-in">
      {/* AttendX branding banner */}
      <div className="rounded-[20px] overflow-hidden border border-[var(--border)]" style={{background:'linear-gradient(135deg,#007AFF 0%,#0051C3 100%)'}}>
        <div className="px-6 py-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-[14px] bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
            <span className="text-white text-xl font-black">A</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[18px] font-black text-white">AttendX</h2>
              <span className="text-[11px] font-semibold text-blue-200">HRM & Attendance Platform</span>
            </div>
            <p className="text-[14px] text-white/80 font-medium truncate">{companyName}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="text-[11px] font-bold px-3 py-1 rounded-full text-white" style={{background:'rgba(255,255,255,0.2)',backdropFilter:'blur(8px)'}}>
              {planMeta.label}
            </span>
            {company.plan_expires_at && (plan === 'PRO' || plan === 'CUSTOM') && (
              <span className="text-[10px] text-white/70">
                Renews {new Date(company.plan_expires_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </span>
            )}
            {plan === 'BASIC' && (
              <button onClick={()=>navigate('/select-plan')} className="flex items-center gap-1 text-[11px] font-semibold text-yellow-300 hover:text-yellow-200 transition-colors">
                <Zap size={11}/> Upgrade to Pro
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Greeting */}
      <div>
        <p className="text-[13px] text-[var(--text-secondary)]">{today}</p>
        <h1 className="text-[24px] font-bold text-[var(--text-primary)]">Good morning, {user?.name?.split(' ')[0]} 👋</h1>
      </div>

      {/* Empty state — no employees yet */}
      {isEmpty && (
        <div className="card flex flex-col items-center text-center py-14 gap-4">
          <div className="w-16 h-16 rounded-[20px] bg-[var(--accent-muted)] flex items-center justify-center">
            <Users size={28} className="text-[var(--accent)]"/>
          </div>
          <div>
            <h2 className="text-[18px] font-bold text-[var(--text-primary)]">Your company is set up!</h2>
            <p className="text-[14px] text-[var(--text-secondary)] mt-1 max-w-sm">
              Add your first employee to start tracking attendance, running payroll, and managing leaves.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap justify-center">
            <button onClick={() => navigate('/employees/new')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-[12px] bg-[var(--accent)] text-white text-[13px] font-semibold hover:opacity-90 transition-opacity">
              <Plus size={15}/> Add First Employee
            </button>
            <button onClick={() => navigate('/settings/plan')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-[12px] bg-[var(--bg-muted)] text-[var(--text-secondary)] text-[13px] font-semibold hover:bg-[var(--border)] transition-colors">
              View Plan <ArrowRight size={14}/>
            </button>
          </div>
        </div>
      )}

      {/* KPI cards + charts — only when company has employees */}
      {!isEmpty && <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Total Employees" value={s.total_employees || 0} icon={Users} color="blue" trend={8} trendLabel="this month"/>
        <StatCard title="Present Today" value={s.present || 0} subtitle={`${presentPct}% attendance`} icon={Clock} color="green" trend={2}/>
        <StatCard title="On Leave" value={s.on_leave || 0} icon={Calendar} color="yellow"/>
        <StatCard title="Late Arrivals" value={s.late_arrivals || 0} icon={Timer} color="red"/>
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Attendance Ring */}
        <Card className="flex flex-col items-center py-6">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-[var(--text-secondary)] mb-4">Today's Attendance</p>
          <div className="relative">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={donutData} cx={75} cy={75} innerRadius={52} outerRadius={72} dataKey="value" strokeWidth={2} stroke="var(--bg-card)">
                  {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]}/>)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[28px] font-black text-[var(--text-primary)]">{presentPct}%</span>
              <span className="text-[11px] text-[var(--text-secondary)]">present</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4">
            {donutData.map((d,i)=>(
              <div key={d.name} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{backgroundColor:DONUT_COLORS[i]}}/>
                <span className="text-[12px] text-[var(--text-secondary)]">{d.name}: <strong className="text-[var(--text-primary)]">{d.value}</strong></span>
              </div>
            ))}
          </div>
        </Card>

        {/* Attendance Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Attendance Trend</CardTitle>
            <span className="text-[12px] text-[var(--text-tertiary)]">Last 8 days</span>
          </CardHeader>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={attendanceTrend} margin={{top:4,right:8,bottom:0,left:-20}}>
              <defs>
                <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#007AFF" stopOpacity={0.15}/>
                  <stop offset="95%" stopColor="#007AFF" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
              <XAxis dataKey="date" tick={{fontSize:11,fill:'var(--text-tertiary)'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'var(--text-tertiary)'}} axisLine={false} tickLine={false} domain={[50,100]} tickFormatter={v=>`${v}%`}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Area type="monotone" dataKey="percentage" name="Attendance" stroke="#007AFF" strokeWidth={2} fill="url(#aGrad)" dot={{fill:'#007AFF',strokeWidth:0,r:3}}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Payroll + Pending actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Payroll Cost Trend */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Payroll Cost Trend</CardTitle>
            <span className="text-[12px] text-[var(--text-tertiary)]">6 months</span>
          </CardHeader>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={payrollTrend} margin={{top:4,right:8,bottom:0,left:-20}} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
              <XAxis dataKey="month" tick={{fontSize:11,fill:'var(--text-tertiary)'}} axisLine={false} tickLine={false}/>
              <YAxis tick={{fontSize:11,fill:'var(--text-tertiary)'}} axisLine={false} tickLine={false} tickFormatter={v=>`₹${v/100000}L`}/>
              <Tooltip content={<CustomTooltip/>}/>
              <Bar dataKey="gross" name="Gross" fill="#007AFF" radius={[4,4,0,0]}/>
              <Bar dataKey="net" name="Net" fill="var(--success)" radius={[4,4,0,0]}/>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:12,paddingTop:8}}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Pending Actions */}
        <Card padding="p-0">
          <div className="px-5 pt-4 pb-3 border-b border-[var(--border)] flex items-center justify-between">
            <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Pending Actions</h3>
            <span className="text-[12px] font-bold text-white bg-[var(--danger)] w-5 h-5 flex items-center justify-center rounded-full">{pendingActions.length}</span>
          </div>
          <div className="divide-y divide-[var(--divider)]">
            {pendingActions.map(a=>{
              const Icon = actionIcons[a.type] || AlertCircle;
              const colors = { leave:'text-[var(--accent)] bg-[var(--accent-muted)]', expense:'text-[var(--success)] bg-[var(--success-muted)]', regularize:'text-[var(--warning)] bg-[var(--warning-muted)]' };
              return (
                <div key={a.id} className="flex items-start gap-3 px-4 py-3.5 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer">
                  <div className={`w-8 h-8 rounded-[8px] flex items-center justify-center shrink-0 ${colors[a.type]||'text-[var(--text-secondary)] bg-[var(--bg-muted)]'}`}>
                    <Icon size={14}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-medium text-[var(--text-primary)] leading-snug">{a.label}</p>
                    <p className="text-[11px] text-[var(--text-tertiary)] mt-0.5">{a.date}</p>
                  </div>
                  {a.urgent && <span className="w-1.5 h-1.5 bg-[var(--danger)] rounded-full mt-1.5 shrink-0"/>}
                </div>
              );
            })}
          </div>
          <div className="px-4 py-3 border-t border-[var(--border)]">
            <button className="text-[12px] font-semibold text-[var(--accent)] hover:underline">View all actions</button>
          </div>
        </Card>
      </div>

      {/* New joiners + Leave distribution */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>New Joiners This Month</CardTitle>
            <span className="text-[12px] text-[var(--text-tertiary)]">{newJoiners.length} employee{newJoiners.length!==1?'s':''}</span>
          </CardHeader>
          {newJoiners.map(j=>(
            <div key={j.id} className="flex items-center gap-3">
              <Avatar name={j.name} size="md"/>
              <div>
                <p className="text-[14px] font-semibold text-[var(--text-primary)]">{j.name}</p>
                <p className="text-[12px] text-[var(--text-secondary)]">{j.designation} · {j.department}</p>
                <p className="text-[11px] text-[var(--text-tertiary)]">Joined {j.joining_date}</p>
              </div>
              <CheckCircle2 size={16} className="ml-auto text-[var(--success)]"/>
            </div>
          ))}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Leave Distribution</CardTitle>
            <span className="text-[12px] text-[var(--text-tertiary)]">This month</span>
          </CardHeader>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={leaveDistribution} cx="50%" cy="50%" innerRadius={45} outerRadius={65} dataKey="value" strokeWidth={2} stroke="var(--bg-card)">
                {leaveDistribution.map((d,i)=><Cell key={i} fill={d.fill}/>)}
              </Pie>
              <Legend iconType="circle" iconSize={8} wrapperStyle={{fontSize:12}}/>
              <Tooltip content={<CustomTooltip/>}/>
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
      </>}
    </div>
  );
}
